import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Mail, Lock, User } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { useToast } from '../hooks/use-toast';
import { LOGO_URL } from '../mock';

export default function Login() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [showPwd, setShowPwd] = useState(false);

  const submit = (type) => (e) => {
    e.preventDefault();
    // mock: persist in localStorage
    localStorage.setItem('avanti_user', JSON.stringify({ loggedIn: true, type, ts: Date.now() }));
    toast({ title: type === 'login' ? 'Willkommen zurück!' : 'Konto erstellt 🎉', description: 'Du bist jetzt angemeldet.' });
    setTimeout(() => navigate('/buchen'), 400);
  };

  return (
    <section className="hero-bg min-h-[calc(100vh-80px)] py-16 flex items-center">
      <div className="max-w-md mx-auto px-5 w-full">
        <div className="card-soft p-8">
          <div className="flex flex-col items-center text-center">
            <div className="h-16 w-16 rounded-2xl bg-white flex items-center justify-center shadow-md overflow-hidden ring-1 ring-teal-100">
              <img src={LOGO_URL} alt="AvantiTutor Logo" className="h-full w-full object-cover" />
            </div>
            <h1 className="mt-4 text-2xl font-extrabold text-slate-900">Willkommen bei AvantiTutor</h1>
            <p className="mt-1 text-sm text-slate-500">Melde dich an oder erstelle ein Konto</p>
          </div>

          <Tabs defaultValue="login" className="mt-6">
            <TabsList className="grid grid-cols-2 bg-teal-50 w-full rounded-full h-11">
              <TabsTrigger value="login" className="rounded-full data-[state=active]:bg-white">Anmelden</TabsTrigger>
              <TabsTrigger value="register" className="rounded-full data-[state=active]:bg-white">Registrieren</TabsTrigger>
            </TabsList>

            <TabsContent value="login" className="mt-5">
              <form onSubmit={submit('login')} className="space-y-4">
                <div className="relative">
                  <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input required type="email" placeholder="E-Mail" className="pl-10 h-11 rounded-xl" />
                </div>
                <div className="relative">
                  <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input required type={showPwd ? 'text' : 'password'} placeholder="Passwort" className="pl-10 pr-10 h-11 rounded-xl" />
                  <button type="button" onClick={() => setShowPwd(!showPwd)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-teal-600">
                    {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <div className="flex justify-between text-sm">
                  <label className="flex items-center gap-2 text-slate-600"><input type="checkbox" className="rounded" /> Angemeldet bleiben</label>
                  <a href="#" className="text-teal-600 hover:underline">Passwort vergessen?</a>
                </div>
                <Button type="submit" className="btn-teal rounded-full h-11 w-full">Anmelden</Button>
              </form>
            </TabsContent>

            <TabsContent value="register" className="mt-5">
              <form onSubmit={submit('register')} className="space-y-4">
                <div className="relative">
                  <User className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input required placeholder="Vollständiger Name" className="pl-10 h-11 rounded-xl" />
                </div>
                <div className="relative">
                  <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input required type="email" placeholder="E-Mail" className="pl-10 h-11 rounded-xl" />
                </div>
                <div className="relative">
                  <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input required type="password" placeholder="Passwort (min. 8 Zeichen)" className="pl-10 h-11 rounded-xl" minLength={8} />
                </div>
                <label className="flex items-start gap-2 text-xs text-slate-600">
                  <input required type="checkbox" className="mt-0.5" />
                  Ich stimme den <a href="#" className="text-teal-600">AGB</a> und <a href="#" className="text-teal-600">Datenschutzbestimmungen</a> zu.
                </label>
                <Button type="submit" className="btn-teal rounded-full h-11 w-full">Konto erstellen</Button>
              </form>
            </TabsContent>
          </Tabs>

          <p className="mt-6 text-center text-sm text-slate-500">
            Noch unsicher? <Link to="/buchen" className="text-teal-600 font-semibold hover:underline">Kostenlos beraten lassen</Link>
          </p>

          <div className="mt-4 p-4 rounded-xl bg-teal-50/70 border border-teal-100 text-center">
            <p className="text-xs text-slate-600">Bestehender Schüler oder Tutor?</p>
            <Link to="/portal" className="text-sm text-teal-700 font-semibold hover:underline inline-flex items-center gap-1 mt-1">
              Zum Schüler-Portal (TutorBird) →
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
